import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControl, IconButton, InputLabel, MenuItem, Paper, Select,
  Stack, Tab, Tabs, TextField, ToggleButton, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import HistoryIcon from '@mui/icons-material/History';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import GridOnIcon from '@mui/icons-material/GridOn';
import StraightenIcon from '@mui/icons-material/Straighten';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TitleIcon from '@mui/icons-material/Title';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ImageIcon from '@mui/icons-material/Image';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import BorderVerticalIcon from '@mui/icons-material/BorderVertical';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import TableRowsIcon from '@mui/icons-material/TableRows';
import CodeIcon from '@mui/icons-material/Code';
import apiService from '../../services/api.service';
import { showSnackbar } from '../../store/slices/uiSlice';
import {
  SoaAppliesTo, SoaBandKey, SoaConfig, SoaElement, SoaElementType, SoaFieldDef, SoaTemplateDefinition,
  SoaTemplateRecord, soaContentWidthMm,
} from '../../types/formattedSoa';
import { soaErrorMessage } from '../../utils/formattedSoa';
import {
  BAND_SHORT, SoaDropTarget, SoaSelection, clamp, elementIds, fitBandToElements, makeColumn, makeElement, newId,
  round2, updateElement, useUndoable,
} from '../../utils/soaDesigner';
import DesignerCanvas from './DesignerCanvas';
import FieldSelector from './FieldSelector';
import PropertiesPanel from './PropertiesPanel';
import CodeEditor from './CodeEditor';
import TemplateSettings from './TemplateSettings';
import DesignerPreview from './DesignerPreview';
import { APPLIES_LABELS, SaveAsDialog, VersionsDialog } from './TemplateDialogs';

const LIST_PATH = '/sales/formatted-soa/templates';
const MAX_COLUMNS = 25;
const MAX_ELEMENTS = 300;

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return true;
  return !!t.closest('[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"], .MuiSelect-select, .MuiPopover-root');
}

interface Meta { name: string; description: string; appliesTo: SoaAppliesTo }

// ── loader ───────────────────────────────────────────────────────────────────────────────────

const SoaTemplateDesigner: React.FC<{ templateId: number; companyId: number }> = ({ templateId, companyId }) => {
  const [record, setRecord] = useState<SoaTemplateRecord | null>(null);
  const [config, setConfig] = useState<SoaConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRecord(null);
    setError(null);
    Promise.all([apiService.getSoaTemplate(templateId), apiService.getSoaConfig(companyId)])
      .then(([t, c]: any[]) => {
        if (cancelled) return;
        const rec = t.data as SoaTemplateRecord;
        if (rec.companyId !== companyId) {
          setError('This template belongs to another company: switch to that company to edit it.');
          return;
        }
        setRecord(rec);
        setConfig(c.data as SoaConfig);
      })
      .catch((e: any) => { if (!cancelled) setError(soaErrorMessage(e, 'The template could not be loaded')); });
    return () => { cancelled = true; };
  }, [templateId, companyId]);

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!record || !config) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!config.permissions.design) {
    return <Alert severity="info" sx={{ m: 2 }}>Template design needs the Statement Templates module (User Management).</Alert>;
  }
  return <DesignerWorkspace key={record.templateId} record={record} config={config} companyId={companyId} />;
};

// ── workspace ────────────────────────────────────────────────────────────────────────────────

const DesignerWorkspace: React.FC<{ record: SoaTemplateRecord; config: SoaConfig; companyId: number }> = ({ record, config, companyId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notify = useCallback(
    (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => dispatch(showSnackbar({ message, severity })),
    [dispatch],
  );
  // The preview sits beside the canvas only on very wide screens; otherwise below it, so the canvas keeps its room.
  const wide = useMediaQuery('(min-width:1880px)');
  const narrow = useMediaQuery('(max-width:899px)');

  const {
    value: def, update, undo, redo, reset, replace, canUndo, canRedo,
  } = useUndoable<SoaTemplateDefinition>(record.definition);
  const [meta, setMeta] = useState<Meta>({ name: record.name, description: record.description, appliesTo: record.appliesTo });
  const [saved, setSaved] = useState<{ def: SoaTemplateDefinition; meta: Meta }>({ def: record.definition, meta: { name: record.name, description: record.description, appliesTo: record.appliesTo } });
  const [isDefault, setIsDefault] = useState(record.isDefault);
  const dirty = def !== saved.def || meta.name !== saved.meta.name || meta.description !== saved.meta.description || meta.appliesTo !== saved.meta.appliesTo;

  const [selection, setSelection] = useState<SoaSelection>(null);
  const [tab, setTab] = useState<'design' | 'code' | 'settings'>('design');
  const [showPreview, setShowPreview] = useState(true);
  const [zoom, setZoom] = useState(3);
  const [fit, setFit] = useState(true);
  const [snapOn, setSnapOn] = useState(true);
  const [gridOn, setGridOn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState<null | 'saveAs' | 'versions'>(null);
  const clipboard = useRef<{ el: SoaElement; band: SoaBandKey } | null>(null);
  const canvasBox = useRef<HTMLDivElement | null>(null);

  const fieldsByKey = useMemo(() => new Map<string, SoaFieldDef>(config.fields.map((f) => [f.key, f])), [config.fields]);
  const contentW = soaContentWidthMm(def);
  const snap = snapOn ? 0.5 : null;

  // Fit the page width to the canvas.
  useEffect(() => {
    const box = canvasBox.current;
    if (!fit || !box) return undefined;
    const apply = () => setZoom(clamp(round2((box.clientWidth - 64) / contentW), 1.2, 8));
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(box);
    return () => ro.disconnect();
  }, [fit, contentW, tab, showPreview, wide]);

  // Leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const applyRecord = useCallback((rec: SoaTemplateRecord, keepHistory: boolean) => {
    if (keepHistory) replace(rec.definition);
    else reset(rec.definition);
    const m = { name: rec.name, description: rec.description, appliesTo: rec.appliesTo };
    setMeta(m);
    setSaved({ def: rec.definition, meta: m });
    setIsDefault(rec.isDefault);
  }, [replace, reset]);

  const save = useCallback(async () => {
    if (saving) return;
    if (!meta.name.trim()) { notify('Give the template a name.', 'warning'); return; }
    setSaving(true);
    try {
      const res: any = await apiService.updateSoaTemplate(record.templateId, {
        name: meta.name.trim(), description: meta.description, appliesTo: meta.appliesTo, definition: def,
      });
      applyRecord(res.data as SoaTemplateRecord, true);
      notify('Template saved');
    } catch (e: any) {
      notify(soaErrorMessage(e, 'The template could not be saved'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, meta, def, record.templateId, applyRecord, notify]);

  const saveAs = async (name: string) => {
    const res: any = await apiService.createSoaTemplate({
      companyId, name, description: meta.description, appliesTo: meta.appliesTo, definition: def,
    });
    setDialog(null);
    notify(`Saved as “${name}”`);
    navigate(`${LIST_PATH}/${res.data.templateId}`);
  };

  const back = () => {
    if (dirty && !window.confirm('Leave without saving your changes?')) return;
    navigate(LIST_PATH);
  };

  // ── adding ────────────────────────────────────────────────────────────────────────────────

  const targetBand = (): SoaBandKey => (selection?.kind === 'element' || selection?.kind === 'band' ? selection.band : 'pageHeader');

  const addElement = (type: SoaElementType, opts: { band?: SoaBandKey; at?: { x: number; y: number }; field?: SoaFieldDef; vertical?: boolean } = {}) => {
    const band = opts.band ?? targetBand();
    if (def.bands[band].elements.length >= MAX_ELEMENTS) { notify(`A band holds at most ${MAX_ELEMENTS} elements.`, 'warning'); return; }
    const bottom = def.bands[band].elements.reduce((m, e) => Math.max(m, e.y + e.h), 0);
    const at = opts.at ?? { x: 0, y: round2(bottom > 0 ? bottom + 1 : 1) };
    let el = makeElement(type, at.x, at.y, opts.field);
    if (type === 'line' && opts.vertical) el = { ...el, orientation: 'vertical', w: 0.5, h: 20 } as SoaElement;
    if (el.x + el.w > contentW) el = { ...el, w: Math.max(5, round2(contentW - el.x)) };
    update((d) => fitBandToElements({
      ...d, bands: { ...d.bands, [band]: { ...d.bands[band], visible: true, elements: [...d.bands[band].elements, el] } },
    }, band));
    setSelection({ kind: 'element', band, id: el.id });
    setTab('design');
  };

  const addColumn = (f: SoaFieldDef, index?: number) => {
    if (def.table.columns.length >= MAX_COLUMNS) { notify(`The table holds at most ${MAX_COLUMNS} columns.`, 'warning'); return; }
    const col = makeColumn(f);
    update((d) => {
      const cols = [...d.table.columns];
      cols.splice(index ?? cols.length, 0, col);
      return { ...d, table: { ...d.table, columns: cols } };
    });
    setSelection({ kind: 'column', id: col.id });
    setTab('design');
  };

  const addField = (f: SoaFieldDef, place?: { band: SoaBandKey; x: number; y: number }) => {
    if (f.group === 'row') { addColumn(f); return; }
    const at = place ? { x: place.x, y: place.y } : undefined;
    if (f.type === 'image') addElement('image', { band: place?.band, at });
    else addElement('field', { band: place?.band, at, field: f });
  };

  const onDropField = (target: SoaDropTarget, key: string) => {
    const f = fieldsByKey.get(key);
    if (!f) return;
    if (target.kind === 'table') {
      if (f.group === 'row') addColumn(f, target.index);
      return;
    }
    if (f.group === 'row') { notify('Table fields become columns: drop them on the table.', 'info'); return; }
    addField(f, target);
  };

  // ── element commands ──────────────────────────────────────────────────────────────────────

  const deleteSelected = () => {
    if (selection?.kind !== 'element') return;
    const { band, id } = selection;
    update((d) => ({ ...d, bands: { ...d.bands, [band]: { ...d.bands[band], elements: d.bands[band].elements.filter((e) => e.id !== id) } } }));
    setSelection(null);
  };

  const pasteElement = (source: SoaElement, band: SoaBandKey, offset: number) => {
    const taken = elementIds(def);
    let id = newId(source.type[0]);
    while (taken.has(id)) id = newId(source.type[0]);
    const copy = { ...JSON.parse(JSON.stringify(source)), id, x: round2(source.x + offset), y: round2(source.y + offset) } as SoaElement;
    update((d) => fitBandToElements({ ...d, bands: { ...d.bands, [band]: { ...d.bands[band], visible: true, elements: [...d.bands[band].elements, copy] } } }, band));
    setSelection({ kind: 'element', band, id });
  };

  const duplicateSelected = () => {
    if (selection?.kind !== 'element') return;
    const el = def.bands[selection.band].elements.find((e) => e.id === selection.id);
    if (el) pasteElement(el, selection.band, 2);
  };

  const nudge = (dx: number, dy: number) => {
    if (selection?.kind !== 'element') return;
    const { band, id } = selection;
    update((d) => fitBandToElements(updateElement(d, band, id, (e) => ({ ...e, x: round2(e.x + dx), y: round2(Math.max(0, e.y + dy)) })), band), `nudge-${id}`);
  };

  // ── keyboard ──────────────────────────────────────────────────────────────────────────────

  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyHandler.current = (e: KeyboardEvent) => {
    const mod = e.ctrlKey || e.metaKey;
    const key = e.key.toLowerCase();
    if (mod && key === 's') { e.preventDefault(); void save(); return; }
    if (dialog || isEditableTarget(e.target)) return;
    if (mod && key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
    if (mod && (key === 'y' || (key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return; }
    if (tab !== 'design') return;
    if (key === 'escape') { setSelection(null); return; }
    if (mod && key === 'v' && clipboard.current) {
      e.preventDefault();
      const band = selection?.kind === 'element' || selection?.kind === 'band' ? selection.band : clipboard.current.band;
      pasteElement(clipboard.current.el, band, band === clipboard.current.band ? 2 : 0);
      return;
    }
    if (selection?.kind !== 'element') return;
    const step = e.shiftKey ? 5 : 0.5;
    if (key === 'arrowleft') { e.preventDefault(); nudge(-step, 0); }
    else if (key === 'arrowright') { e.preventDefault(); nudge(step, 0); }
    else if (key === 'arrowup') { e.preventDefault(); nudge(0, -step); }
    else if (key === 'arrowdown') { e.preventDefault(); nudge(0, step); }
    else if (key === 'delete' || key === 'backspace') { e.preventDefault(); deleteSelected(); }
    else if (mod && key === 'd') { e.preventDefault(); duplicateSelected(); }
    else if (mod && key === 'c') {
      const el = def.bands[selection.band].elements.find((x) => x.id === selection.id);
      if (el) clipboard.current = { el: JSON.parse(JSON.stringify(el)), band: selection.band };
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => keyHandler.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ── layout ────────────────────────────────────────────────────────────────────────────────

  const toolbar: Array<{ type: SoaElementType; label: string; icon: React.ReactNode; vertical?: boolean }> = [
    { type: 'text', label: 'Text', icon: <TitleIcon fontSize="small" /> },
    { type: 'field', label: 'Field', icon: <DataObjectIcon fontSize="small" /> },
    { type: 'image', label: 'Logo / image', icon: <ImageIcon fontSize="small" /> },
    { type: 'line', label: 'Line', icon: <HorizontalRuleIcon fontSize="small" /> },
    { type: 'line', label: 'Vertical line', icon: <BorderVerticalIcon fontSize="small" />, vertical: true },
    { type: 'box', label: 'Box', icon: <CropSquareIcon fontSize="small" /> },
    { type: 'kv', label: 'Label / value', icon: <TableRowsIcon fontSize="small" /> },
    { type: 'html', label: 'HTML', icon: <CodeIcon fontSize="small" /> },
  ];

  const preview = showPreview ? (
    <DesignerPreview companyId={companyId} definition={def} config={config} height={wide ? 'calc(100vh - 330px)' : '75vh'} />
  ) : null;

  const areaHeight = 'calc(100vh - 215px)';

  const designArea = (
    <Box sx={{ display: 'flex', height: areaHeight, minHeight: 520, border: '1px solid #dfe3e8', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ width: 230, flex: 'none', borderRight: '1px solid #dfe3e8', minHeight: 0 }}>
        <FieldSelector fields={config.fields} onAdd={(f) => addField(f)} targetLabel={BAND_SHORT[targetBand()]} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 1, py: 0.5, borderBottom: '1px solid #dfe3e8', bgcolor: '#fafbfc' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Add to {BAND_SHORT[targetBand()]}:</Typography>
          {toolbar.map((t) => (
            <Tooltip key={t.label} title={`Add ${t.label.toLowerCase()}`}>
              <Button size="small" startIcon={t.icon} onClick={() => addElement(t.type, { vertical: t.vertical })} sx={{ textTransform: 'none', minWidth: 0, px: 0.75 }}>
                {t.label}
              </Button>
            </Tooltip>
          ))}
        </Stack>
        <Box ref={canvasBox} sx={{ flex: 1, overflow: 'auto', bgcolor: '#eef0f3', minHeight: 0 }}>
          <DesignerCanvas
            def={def}
            zoom={zoom}
            selection={selection}
            onSelect={setSelection}
            onChange={update}
            fields={fieldsByKey}
            snap={snap}
            showGrid={gridOn}
            onDropField={onDropField}
          />
        </Box>
      </Box>
      <Box sx={{ width: 330, flex: 'none', borderLeft: '1px solid #dfe3e8', overflow: 'auto' }}>
        <PropertiesPanel
          def={def}
          selection={selection}
          onSelect={setSelection}
          onChange={update}
          config={config}
          fieldsByKey={fieldsByKey}
          onDuplicate={duplicateSelected}
          onDelete={deleteSelected}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
      {narrow && <Alert severity="info" sx={{ mb: 1 }}>The designer needs a wider screen (900 px or more) to be comfortable.</Alert>}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Tooltip title="Back to the templates">
          <IconButton aria-label="Back to the templates" onClick={back}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <TextField size="small" label="Template name" value={meta.name} inputProps={{ maxLength: 120 }} sx={{ minWidth: 240 }}
          error={!meta.name.trim()} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} />
        <FormControl size="small" sx={{ width: 210 }}>
          <InputLabel>For</InputLabel>
          <Select label="For" value={meta.appliesTo} onChange={(e) => setMeta((m) => ({ ...m, appliesTo: e.target.value as SoaAppliesTo }))}>
            {(Object.keys(APPLIES_LABELS) as SoaAppliesTo[]).map((k) => <MenuItem key={k} value={k}>{APPLIES_LABELS[k]}</MenuItem>)}
          </Select>
        </FormControl>
        {isDefault && <Chip size="small" color="primary" variant="outlined" label="Default" />}
        {dirty && <Chip size="small" color="warning" label="Unsaved" />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Undo (Ctrl+Z)"><span><IconButton aria-label="Undo" onClick={undo} disabled={!canUndo}><UndoIcon /></IconButton></span></Tooltip>
        <Tooltip title="Redo (Ctrl+Y)"><span><IconButton aria-label="Redo" onClick={redo} disabled={!canRedo}><RedoIcon /></IconButton></span></Tooltip>
        <Button startIcon={<HistoryIcon />} onClick={() => setDialog('versions')}>Versions</Button>
        <Button onClick={() => setDialog('saveAs')}>Save as…</Button>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={() => { void save(); }} disabled={saving || !dirty}>
          Save
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1, borderBottom: '1px solid #dfe3e8' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40 } }}>
          <Tab value="design" label="Design" />
          <Tab value="code" label="HTML / CSS / JS" />
          <Tab value="settings" label="Settings" />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        {tab === 'design' && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Zoom out"><IconButton size="small" aria-label="Zoom out" onClick={() => { setFit(false); setZoom((z) => clamp(round2(z / 1.2), 1.2, 8)); }}><ZoomOutIcon fontSize="small" /></IconButton></Tooltip>
            <Typography variant="caption" sx={{ width: 42, textAlign: 'center' }}>{Math.round((zoom / (96 / 25.4)) * 100)}%</Typography>
            <Tooltip title="Zoom in"><IconButton size="small" aria-label="Zoom in" onClick={() => { setFit(false); setZoom((z) => clamp(round2(z * 1.2), 1.2, 8)); }}><ZoomInIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Fit the page width"><ToggleButton size="small" value="fit" selected={fit} aria-label="Fit the page width" onChange={() => setFit((f) => !f)}><FitScreenIcon fontSize="small" /></ToggleButton></Tooltip>
            <Tooltip title="Snap to 0.5 mm"><ToggleButton size="small" value="snap" selected={snapOn} aria-label="Snap to 0.5 mm" onChange={() => setSnapOn((s) => !s)}><StraightenIcon fontSize="small" /></ToggleButton></Tooltip>
            <Tooltip title="5 mm grid"><ToggleButton size="small" value="grid" selected={gridOn} aria-label="Show the 5 mm grid" onChange={() => setGridOn((g) => !g)}><GridOnIcon fontSize="small" /></ToggleButton></Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </Stack>
        )}
        <ToggleButton size="small" value="preview" selected={showPreview} aria-label="Show the preview" onChange={() => setShowPreview((p) => !p)} sx={{ mb: 0.5 }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} /> Preview
        </ToggleButton>
      </Stack>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {tab === 'design' && designArea}
          {tab === 'code' && (
            <Paper variant="outlined">
              <CodeEditor css={def.custom.css} js={def.custom.js} onChange={(patch) => update((d) => ({ ...d, custom: { ...d.custom, ...patch } }), `code-${Object.keys(patch)[0]}`)} />
            </Paper>
          )}
          {tab === 'settings' && (
            <Paper variant="outlined">
              <TemplateSettings def={def} onChange={update} fonts={config.fonts} description={meta.description}
                onDescription={(v) => setMeta((m) => ({ ...m, description: v }))} />
            </Paper>
          )}
        </Box>
        {preview && wide && (
          <Paper variant="outlined" sx={{ width: '36%', flex: 'none', maxHeight: areaHeight, overflow: 'auto' }}>{preview}</Paper>
        )}
      </Box>
      {preview && !wide && <Paper variant="outlined" sx={{ mt: 1.5 }}>{preview}</Paper>}

      <SaveAsDialog open={dialog === 'saveAs'} defaultName={`${meta.name} (copy)`.slice(0, 120)} onClose={() => setDialog(null)} onSave={saveAs} />
      <VersionsDialog
        open={dialog === 'versions'}
        templateId={record.templateId}
        dirty={dirty}
        onClose={() => setDialog(null)}
        onRestored={(rec) => { applyRecord(rec, true); setDialog(null); setSelection(null); notify('Version restored'); }}
      />
    </Box>
  );
};

export default SoaTemplateDesigner;
